/** Setup's list of addresses staff can forward customer messages to. Admins add and remove; others read. */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { apiErrorText } from '../../lib/apiErrorText'
import { TITLE_LABELS, TITLE_ORDER, parseAddresses, type DeskTitle, type ForwardAddress } from '../../lib/supportDesk'

const FIELD = 'bg-ink-900 border border-ink-500 px-2 py-1 text-paper font-mono text-[13px]'

export function ForwardAddressList({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient()
  const list = useQuery<ForwardAddress[]>({
    queryKey: ['support', 'forward-addresses', 'all'],
    queryFn: () => api.get<ForwardAddress[]>('/api/support/forward-addresses'),
  })
  const [label, setLabel] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState<'' | DeskTitle>('')
  const emailOk = parseAddresses(email).valid.length === 1 && parseAddresses(email).invalid.length === 0
  const add = useMutation({
    mutationFn: () => api.post<ForwardAddress>('/api/support/forward-addresses',
      { label: label.trim(), email: email.trim(), publication: title || null }),
    onSuccess: () => { setLabel(''); setEmail(''); setTitle(''); qc.invalidateQueries({ queryKey: ['support', 'forward-addresses'] }) },
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/api/support/forward-addresses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support', 'forward-addresses'] }),
  })
  const rows = list.data ?? []

  return (
    <section aria-label="Forward addresses" className="space-y-3 max-w-[900px]">
      <h2 className="font-serif text-2xl text-paper">Forward addresses</h2>
      <p className="text-sm text-paper-dim">
        People staff can forward a customer's message to, picked with one click on the Forward panel. Staff can also
        type any other address there. An address for one title only shows on that title's conversations.
      </p>
      {list.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(list.error)}</p>}
      {list.isSuccess && rows.length === 0 && <p className="text-paper-trace text-sm">None yet.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="label text-left"><th className="pb-2 pr-4 font-normal">Name</th><th className="pb-2 pr-4 font-normal">Email</th><th className="pb-2 pr-4 font-normal">Titles</th>{isAdmin && <th />}</tr></thead>
            <tbody>
              {rows.map(a => (
                <tr key={a.id} className="border-t border-ink-500">
                  <td className="py-2 pr-4 text-paper">{a.label}</td>
                  <td className="py-2 pr-4 font-mono text-[13px] text-paper-dim">
                    {a.email}{!a.internal && <span className="ml-2 text-[10px] uppercase text-amber-300">outside</span>}
                  </td>
                  <td className="py-2 pr-4 text-paper-dim">{a.publication ? TITLE_LABELS[a.publication] : 'All titles'}</td>
                  {isAdmin && (
                    <td className="py-2 text-right">
                      <button type="button" aria-label={`Remove ${a.label}`} disabled={remove.isPending}
                        onClick={() => { if (window.confirm(`Remove ${a.label} (${a.email}) from the forward list?`)) remove.mutate(a.id) }}
                        className="label hover:text-red-400 disabled:opacity-40">Remove</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(remove.error)}</p>}

      {isAdmin && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={e => { e.preventDefault(); if (label.trim() && emailOk) add.mutate() }}>
          <label className="flex flex-col gap-1"><span className="label">Name</span>
            <input className={FIELD} value={label} onChange={e => setLabel(e.target.value)} maxLength={120} placeholder="Fulfilment" />
          </label>
          <label className="flex flex-col gap-1"><span className="label">Email</span>
            <input className={`${FIELD} min-w-[240px]`} value={email} onChange={e => setEmail(e.target.value)} maxLength={320}
              placeholder="orders@example.com" type="email" />
          </label>
          <label className="flex flex-col gap-1"><span className="label">Titles</span>
            <select className={FIELD} value={title} onChange={e => setTitle(e.target.value as '' | DeskTitle)}>
              <option value="">All titles</option>
              {TITLE_ORDER.map(t => <option key={t} value={t}>{TITLE_LABELS[t]}</option>)}
            </select>
          </label>
          <button type="submit" disabled={!label.trim() || !emailOk || add.isPending}
            className="label-amber border border-amber-glow px-3 py-1.5 disabled:opacity-40">{add.isPending ? 'Adding…' : 'Add'}</button>
          {add.isError && <p role="alert" className="basis-full text-red-400 text-sm">{apiErrorText(add.error)}</p>}
        </form>
      )}
    </section>
  )
}
